<template>
  <!-- Detailed mock cart for the Live Mobile preview: groups the shared cart
       (`sim.cart`) by Order vs Quote + PO/Reference, each line expandable to show
       company / machine / assembly / sub-assembly / part + adjustable qty + line
       total, with per-group subtotals and a grand total. -->
  <div class="cc">
    <div v-if="!groups.length" class="cc-empty">No items yet — add parts above.</div>

    <div v-for="g in groups" :key="g.key" class="cc-group">
      <div class="cc-group-head" :class="g.dest === 'Quote' ? 'is-quote' : 'is-order'">
        <span class="cc-group-label">
          {{ g.dest === 'Quote' ? '📝 Quote' : '🧾 Order' }}
          <template v-if="g.ref"> · {{ g.dest === 'Quote' ? 'Ref' : 'PO' }} #{{ g.ref }}</template>
          <template v-else> · <em>no {{ g.dest === 'Quote' ? 'reference' : 'PO' }}</em></template>
        </span>
        <span class="cc-group-sub">${{ money(g.subtotal) }}</span>
      </div>

      <div v-for="(it, idx) in g.items" :key="idx" class="cc-item">
        <div class="cc-item-row" @click.stop="toggle(it)">
          <span class="cc-caret">{{ it._open ? '▾' : '▸' }}</span>
          <span class="cc-item-name"><b>{{ it.sku }}</b> <span class="cc-mut">×{{ it.qty }}</span></span>
          <span class="cc-item-line">${{ money(it.price * it.qty) }}</span>
        </div>
        <div v-if="it._open" class="cc-item-detail">
          <div class="cc-kv"><span>Company</span><b>{{ it.company || '—' }}</b></div>
          <div class="cc-kv"><span>Machine</span><b>{{ it.machine || '—' }}</b></div>
          <div class="cc-kv"><span>Assembly</span><b>{{ it.assembly || '—' }}</b></div>
          <div class="cc-kv"><span>Sub-assembly</span><b>{{ it.sub || '—' }}</b></div>
          <div class="cc-kv"><span>Part</span><b>{{ it.sku }} — {{ it.name }}</b></div>
          <div class="cc-kv"><span>Unit price</span><b>${{ money(it.price) }}</b></div>
          <div class="cc-kv">
            <span>Quantity</span>
            <span class="cc-qty">
              <button @click.stop="dec(it)">−</button><b>{{ it.qty }}</b><button @click.stop="it.qty++">+</button>
            </span>
          </div>
          <div class="cc-kv cc-line"><span>Line total</span><b>${{ money(it.price * it.qty) }}</b></div>
          <button class="cc-rm" @click.stop="remove(it)">Remove</button>
        </div>
      </div>
    </div>

    <div v-if="groups.length" class="cc-grand">
      <span>Grand total <small>· {{ orderCount }} order{{ orderCount === 1 ? '' : 's' }}, {{ quoteCount }} quote{{ quoteCount === 1 ? '' : 's' }}</small></span>
      <b>${{ money(grand) }}</b>
    </div>
    <button v-if="groups.length" class="cc-place" @click.stop="$emit('place')">Review &amp; Place</button>
  </div>
</template>

<script>
export default {
  name: "ComposerCart",
  props: {
    sim: {
      type: Object,
      default: function () {
        return { cart: [] };
      },
    },
  },
  computed: {
    items() {
      return (this.sim && this.sim.cart) || [];
    },
    // Group by Order/Quote + PO/Reference, preserving first-seen order.
    groups() {
      var out = [];
      var byKey = {};
      var list = this.items;
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        var dest = it.dest || "Order";
        var ref = it.ref || "";
        var key = dest + "|" + ref;
        if (!byKey[key]) {
          byKey[key] = { key: key, dest: dest, ref: ref, items: [], subtotal: 0 };
          out.push(byKey[key]);
        }
        byKey[key].items.push(it);
        byKey[key].subtotal += (it.price || 0) * (it.qty || 0);
      }
      return out;
    },
    grand() {
      return this.items.reduce(function (s, it) {
        return s + (it.price || 0) * (it.qty || 0);
      }, 0);
    },
    orderCount() {
      return this.groups.filter(function (g) {
        return g.dest !== "Quote";
      }).length;
    },
    quoteCount() {
      return this.groups.filter(function (g) {
        return g.dest === "Quote";
      }).length;
    },
  },
  methods: {
    money(n) {
      return (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    toggle(it) {
      this.$set(it, "_open", !it._open);
    },
    dec(it) {
      it.qty = Math.max(1, it.qty - 1);
    },
    remove(it) {
      var i = this.items.indexOf(it);
      if (i >= 0) this.items.splice(i, 1);
    },
  },
};
</script>

<style scoped>
.cc {
  font-family: var(--cmp-font, -apple-system, "Segoe UI", Roboto, Arial, sans-serif);
  font-size: var(--cmp-fs, 13px);
  color: var(--cmp-color, #1f2733);
}
.cc-empty {
  color: #888;
  padding: 6px 0;
}
.cc-group {
  border: 1px solid #e2e5e8;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
}
.cc-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  font-weight: 700;
  color: #fff;
}
.cc-group-head.is-order {
  background: #c8302a;
}
.cc-group-head.is-quote {
  background: #2a2f36;
}
.cc-group-label em {
  font-style: normal;
  opacity: 0.8;
  font-weight: 500;
}
.cc-item {
  border-top: 1px solid #f1f1f1;
}
.cc-item-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  cursor: pointer;
}
.cc-caret {
  color: #c8302a;
  font-size: 11px;
}
.cc-item-name {
  flex: 1 1 auto;
  min-width: 0;
}
.cc-mut {
  color: #888;
}
.cc-item-line {
  white-space: nowrap;
  font-weight: 600;
}
.cc-item-detail {
  background: #fafbfc;
  padding: 8px 10px 10px;
  border-top: 1px dashed #e2e5e8;
}
.cc-kv {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 3px 0;
  font-size: 12px;
}
.cc-kv span {
  color: #888;
}
.cc-kv b {
  text-align: right;
}
.cc-line {
  border-top: 1px solid #eceff2;
  margin-top: 4px;
  padding-top: 6px;
}
.cc-qty {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cc-qty button {
  width: 22px;
  height: 22px;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  /* Explicit black — independent of the editor's text color, so the − / +
     stay readable on their light button even when cart text is set white. */
  color: #000;
}
.cc-rm {
  margin-top: 8px;
  border: 1px solid #c8302a;
  color: #c8302a;
  background: #fff;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  font-weight: 600;
}
.cc-grand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 2px solid #2a2f36;
  font-weight: 800;
  font-size: 14px;
}
.cc-grand small {
  color: #888;
  font-weight: 500;
}
.cc-place {
  width: 100%;
  margin-top: 10px;
  border: 0;
  border-radius: 6px;
  background: #c8302a;
  color: #fff;
  font-weight: 700;
  padding: 10px;
  cursor: pointer;
}
</style>
